import { Fragment, useEffect, useReducer } from 'react'
import {
  ChatAltIcon,
  TagIcon,
  UserCircleIcon,
  PencilAltIcon,
  BriefcaseIcon,
} from '@heroicons/react/solid'
import classNames from 'classnames'
import { Disclosure } from '@headlessui/react'
import { useForm, useFieldArray } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

type Education = {
  school: string
  degree: string
  field_of_study: string
  start_date: string
  end_date: string
}

type State = {
  education: Education[]
}

const initialState = {
  education: [],
}

function reducer(state: State, action: { type: string; payload?: any }) {
  if (action.type === 'add') {
    return {
      ...state,
      education: [
        ...state.education,
        {
          school: '',
          degree: '',
          field_of_study: '',
          start_date: '',
          end_date: '',
        },
      ],
    }
  }
  if (action.type === 'update') {
    return {
      ...state,
      education: state.education.map((entry, index) => {
        if (index !== action.payload.i) {
          return entry
        }

        return {
          ...action.payload.values,
          current: action.payload.values.current === 'on',
        }
      }),
    }
  }
  if (action.type === 'delete') {
    return {
      ...state,
      education: [
        ...state.education.slice(0, action.payload),
        ...state.education.slice(action.payload + 1),
      ],
    }
  }
  return state
}

const schema = z.object({
  education: z.array(
    z.object({
      school: z.string().min(1, 'This field is required'),
      degree: z.string().optional(),
      field_of_study: z.string().optional(),
      start_date: z.preprocess(
        (val: unknown) => (!val || (val as string).length === 0 ? null : val),
        z
          .string()
          .regex(
            /[0-9]{4}-[0-9]{2}-[0-9]{2}/i,
            'Invalid format, please use YYYY-MM-DD'
          )
          .nullish()
      ),
      end_date: z.preprocess(
        (val: unknown) => (!val || (val as string).length === 0 ? null : val),
        z
          .string()
          .regex(
            /[0-9]{4}-[0-9]{2}-[0-9]{2}/i,
            'Invalid format, please use YYYY-MM-DD'
          )
          .nullish()
      ),
    })
  ),
})

export function EducationFormInputWorkable() {
  const [state, dispatch] = useReducer(reducer, initialState)

  const {
    control,
    register,
    trigger,
    formState: { errors },
    getValues,
  } = useForm<{
    education: Education[]
  }>({
    resolver: zodResolver(schema),
  })
  const { fields, append, prepend, remove, swap, move, insert } = useFieldArray(
    {
      control,
      name: 'education',
    }
  )

  const onSubmit = async (i: number, close: () => void) => {
    const validationResult = await trigger()

    if (validationResult) {
      dispatch({
        type: 'update',
        payload: { i, values: getValues(`education.${i}`) },
      })

      close()
    }
  }

  return (
    <div>
      <div className="-my-5 mt-6 flow-root divide-y divide-gray-200">
        {state.education.map((entry, i) => (
          <Disclosure key={i} as={'div'} className="" defaultOpen>
            <div className="py-4">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100">
                    <BriefcaseIcon className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {entry.school}
                  </p>
                  <p className="truncate text-sm text-gray-500">
                    {entry.degree} {entry.field_of_study}
                  </p>
                </div>
                <div>
                  <Disclosure.Button className="inline-flex items-center rounded-full border border-gray-300 bg-white px-2.5 py-0.5 text-sm font-medium leading-5 text-gray-700 shadow-sm hover:bg-gray-50">
                    {({ open: isOpen }: { open: boolean }) => (
                      <>{isOpen ? 'cancel' : 'edit'}</>
                    )}
                  </Disclosure.Button>
                </div>
              </div>
            </div>

            <Disclosure.Panel unmount={false}>
              {({ close }) => (
                <div className="p-5">
                  <div className="">
                    <label className="block text-sm font-medium text-gray-700">
                      School<span className="text-rose-500"> *</span>
                    </label>
                    <div className="mt-1">
                      <input
                        {...register(`education.${i}.school` as const)}
                        type="text"
                        autoComplete="off"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-accent-color focus:ring-accent-color sm:text-sm"
                      />
                      {errors?.education && errors.education[i]?.school && (
                        <div className="pt-1 text-red-700" id="password-error">
                          {errors?.education[i]?.school?.message}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Degree
                    </label>
                    <div className="mt-1">
                      <input
                        {...register(`education.${i}.degree` as const)}
                        type="text"
                        autoComplete="off"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-accent-color focus:ring-accent-color sm:text-sm"
                      />
                      {errors?.education && errors.education[i]?.degree && (
                        <div className="pt-1 text-red-700" id="password-error">
                          {errors?.education[i]?.degree?.message}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Field of study
                    </label>
                    <div className="mt-1">
                      <input
                        {...register(`education.${i}.field_of_study` as const)}
                        type="text"
                        autoComplete="off"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-accent-color focus:ring-accent-color sm:text-sm"
                      />
                      {errors?.education &&
                        errors.education[i]?.field_of_study && (
                          <div
                            className="pt-1 text-red-700"
                            id="password-error"
                          >
                            {errors?.education[i]?.field_of_study?.message}
                          </div>
                        )}
                    </div>
                  </div>
                  <div className="flex">
                    <div className="mt-4 w-1/2 pr-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Start date
                      </label>
                      <div className="mt-1">
                        <input
                          {...register(`education.${i}.start_date` as const)}
                          type="text"
                          autoComplete="off"
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-accent-color focus:ring-accent-color sm:text-sm"
                        />
                        <p
                          className="mt-1 text-xs text-gray-500"
                          id="email-description"
                        >
                          YYYY-MM-DD
                        </p>
                        {errors?.education && errors.education[i]?.start_date && (
                          <div
                            className="pt-1 text-red-700"
                            id="password-error"
                          >
                            {errors?.education[i]?.start_date?.message}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-4 w-1/2 pl-4">
                      <label className="block text-sm font-medium text-gray-700">
                        End date
                      </label>
                      <div className="mt-1">
                        <input
                          {...register(`education.${i}.end_date` as const)}
                          type="text"
                          autoComplete="off"
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-accent-color focus:ring-accent-color sm:text-sm"
                        />
                        <p
                          className="mt-1 text-xs text-gray-500"
                          id="email-description"
                        >
                          YYYY-MM-DD
                        </p>
                        {errors?.education && errors.education[i]?.end_date && (
                          <div
                            className="pt-1 text-red-700"
                            id="password-error"
                          >
                            {errors?.education[i]?.end_date?.message}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        dispatch({ type: 'delete', payload: i })
                        remove(i)
                      }}
                      className='className="inline-flex items-center rounded-full border border-rose-300 bg-white px-2.5 py-0.5 text-sm font-medium leading-5 text-rose-400 shadow-sm hover:border-rose-500'
                    >
                      delete
                    </button>
                    <button
                      onClick={(event) => {
                        event.preventDefault()
                        onSubmit(i, close)
                      }}
                      className="ml-2 inline-flex items-center rounded-full border border-gray-300 bg-white px-2.5 py-0.5 text-sm font-medium leading-5 text-gray-700 shadow-sm hover:bg-gray-50"
                    >
                      Save
                    </button>
                  </div>
                </div>
              )}
            </Disclosure.Panel>
          </Disclosure>
        ))}
      </div>
      <div className="mt-6">
        <button
          onClick={(e) => {
            e.preventDefault()
            dispatch({ type: 'add' })
            append({})
          }}
          className="mx-auto flex w-96 items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
        >
          Add degree
        </button>
      </div>
    </div>
  )
}
